import { z } from 'zod';

export enum FontWeights {
  Light = 'light',
  Regular = 'regular',
  Medium = 'medium',
  Semibold = 'semibold',
  Bold = 'bold',
  Variable = 'variable',
}

export enum TypographySizes {
  Body = 'body',
  Cta = 'cta',
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3',
  H4 = 'h4',
  Link = 'link',
}

const CTASchema = z.object({
  backgroundColor: z.string(),
  borderColor: z.string(),
  borderRadius: z.string(),
  borderStyle: z.string(),
  borderWidth: z.string(),
  color: z.string(),
  fontWeight: z.string(),
  letterSpacing: z.string(),
  paddingBottom: z.string(),
  paddingLeft: z.string(),
  paddingRight: z.string(),
  paddingTop: z.string(),
  transition: z.string(),
  hoverBackgroundColor: z.string(),
  hoverColor: z.string(),
});

const FontSchema = z.object({
  fontFamily: z.string(),
  fontWeight: z.string(),
  src: z.string(),
});

const TypographySchema = z.object({
  fontFamily: z.string(),
  fontSize: z.string(),
  fontWeight: z.string(),
  letterSpacing: z.string(),
  lineHeight: z.string(),
});

export const ThemePropertiesSchema = z.object({
  version: z.literal(2),
  card: z.object({
    alignItems: z.string(),
    backgroundColor: z.string(),
    borderColor: z.string(),
    borderRadius: z.string(),
    borderStyle: z.string(),
    borderWidth: z.string(),
    boxShadow: z.string(),
  }),
  color: z.object({
    brandDark: z.string(),
    brandMedium: z.string(),
    brandLight: z.string(),
    default: z.string(),
    textDark: z.string(),
    textLight: z.string(),
    none: z.string(),
    unset: z.string(),
  }),
  cta: z.object({
    primary: CTASchema,
    secondary: CTASchema,
    tertiary: CTASchema,
  }),
  fontFace: z
    .object({
      primary: z.object({
        [FontWeights.Light]: FontSchema,
        [FontWeights.Regular]: FontSchema,
        [FontWeights.Medium]: FontSchema,
        [FontWeights.Semibold]: FontSchema,
        [FontWeights.Bold]: FontSchema,
        [FontWeights.Variable]: FontSchema,
      }),
    })
    .optional(),
  fontWeight: z.object({
    [FontWeights.Light]: z.string(),
    [FontWeights.Regular]: z.string(),
    [FontWeights.Medium]: z.string(),
    [FontWeights.Semibold]: z.string(),
    [FontWeights.Bold]: z.string(),
    [FontWeights.Variable]: z.string(),
  }),
  image: z.object({
    borderColor: z.string(),
    borderRadius: z.string(),
    borderStyle: z.string(),
    borderWidth: z.string(),
    boxShadow: z.string(),
  }),
  spacing: z
    .object({
      '000': z.string(),
      '025': z.string(),
      '050': z.string(),
      '075': z.string(),
      '100': z.string(),
      '150': z.string(),
      '200': z.string(),
      '250': z.string(),
      '300': z.string(),
      '400': z.string(),
      '500': z.string(),
      '600': z.string(),
      '700': z.string(),
      '800': z.string(),
      '900': z.string(),
      '1000': z.string(),
      '1200': z.string(),
      '1600': z.string().default('128'),
    })
    .optional(),
  spacingBase: z.coerce.string().default('8'),
  typography: z.object({
    [TypographySizes.Body]: TypographySchema,
    [TypographySizes.Cta]: TypographySchema,
    [TypographySizes.Link]: TypographySchema.extend({ textDecoration: z.string() }),
    [TypographySizes.H4]: TypographySchema,
    [TypographySizes.H3]: TypographySchema,
    [TypographySizes.H2]: TypographySchema,
    [TypographySizes.H1]: TypographySchema,
  }),
});

export type ThemeProperties = z.infer<typeof ThemePropertiesSchema>;
